import { useState } from "react";
import DSBox from "views/system-design/DSBox";
import DSTypography from "views/system-design/DSTypography";
import DSChip from "views/system-design/DSChip";
import {
  DSTableContainer,
  DSTable,
  DSTableHead,
  DSTableBody,
  DSTableRow,
  DSTableCell,
} from "views/system-design/Table";
import ArchiveIcon from "@mui/icons-material/Archive";
import { Ticket, TYPE_COLORS, STATUS_COLORS } from "../../types";
import useUsers from "components/hooks/useUsers";
import {
  formatDate,
  getTypeLabel,
  getStatusLabel,
  resolveUserName,
} from "../../utils";
import {
  TicketSearchParams,
  useTicketSearch,
} from "../../hooks/useTicketQueries";
import { useIsMobile } from "../../hooks/useIsMobile";
import LoadMoreFooter from "../shared/LoadMoreFooter";
import TicketDetailModal from "../detail/TicketDetailModal";

interface ArchiveViewProps {
  searchParameters: TicketSearchParams;
}

export default function ArchiveView({ searchParameters }: ArchiveViewProps) {
  const { data: allUsers } = useUsers();
  const users = allUsers ?? [];
  const [selectedTicket, setSelectedTicket] = useState<Ticket | null>(null);
  const isMobile = useIsMobile();

  const {
    tickets: archivedTickets,
    total,
    loadedCount,
    hasMore,
    loadMore,
  } = useTicketSearch(searchParameters);
  const searchQuery = searchParameters.query ?? "";

  const resolvedDate = (ticket: Ticket): string => {
    const dateString = ticket.resolvedAt ?? ticket.updatedAt;
    return dateString ? formatDate(dateString) : "-";
  };

  return (
    <DSBox
      sx={{ p: 2, height: "100%", display: "flex", flexDirection: "column" }}
    >
      <DSBox
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          mb: 2,
        }}
      >
        <ArchiveIcon sx={{ color: "text.secondary" }} />
        <DSTypography variant="h6">Archive</DSTypography>
        <DSTypography variant="body2" sx={{ color: "text.secondary" }}>
          Tickets archived manually or 2 weeks after completion
        </DSTypography>
      </DSBox>

      {archivedTickets.length === 0 ? (
        <DSBox
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            flex: 1,
            color: "text.secondary",
          }}
        >
          <ArchiveIcon sx={{ fontSize: 48, mb: 1, opacity: 0.4 }} />
          <DSTypography>
            {searchQuery
              ? "No archived tickets match your search"
              : "No archived tickets yet"}
          </DSTypography>
        </DSBox>
      ) : (
        <DSTableContainer sx={{ flex: 1, overflow: "auto" }}>
          <DSTable stickyHeader>
            <DSTableHead>
              <DSTableRow>
                <DSTableCell
                  sx={{
                    width: isMobile ? "15%" : "8%",
                    fontWeight: 600,
                  }}
                >
                  Key
                </DSTableCell>
                <DSTableCell
                  sx={{
                    width: isMobile ? "45%" : "25%",
                    fontWeight: 600,
                  }}
                >
                  Title
                </DSTableCell>
                {!isMobile && (
                  <DSTableCell sx={{ width: "8%", fontWeight: 600 }}>
                    Type
                  </DSTableCell>
                )}
                <DSTableCell
                  sx={{
                    width: isMobile ? "20%" : "10%",
                    fontWeight: 600,
                  }}
                >
                  Status
                </DSTableCell>
                {!isMobile && (
                  <DSTableCell sx={{ width: "13%", fontWeight: 600 }}>
                    Labels
                  </DSTableCell>
                )}
                <DSTableCell
                  sx={{
                    width: isMobile ? "20%" : "13%",
                    fontWeight: 600,
                  }}
                >
                  Assignee
                </DSTableCell>
                {!isMobile && (
                  <DSTableCell sx={{ width: "10%", fontWeight: 600 }}>
                    Resolved
                  </DSTableCell>
                )}
              </DSTableRow>
            </DSTableHead>
            <DSTableBody>
              {archivedTickets.map((ticket) => (
                <DSTableRow
                  key={ticket.id}
                  onClick={() => setSelectedTicket(ticket)}
                  sx={{
                    cursor: "pointer",
                    "&:hover": { bgcolor: "action.hover" },
                  }}
                >
                  <DSTableCell
                    sx={{ fontWeight: 600, color: "text.secondary" }}
                  >
                    {ticket.key}
                  </DSTableCell>
                  <DSTableCell>{ticket.title}</DSTableCell>
                  {!isMobile && (
                    <DSTableCell>
                      <DSChip
                        label={getTypeLabel(ticket.type)}
                        size="small"
                        sx={{
                          bgcolor: TYPE_COLORS[ticket.type] || "grey.600",
                          color: "common.white",
                          fontWeight: 500,
                          fontSize: 11,
                        }}
                      />
                    </DSTableCell>
                  )}
                  <DSTableCell>
                    <DSChip
                      label={getStatusLabel(ticket.status)}
                      size="small"
                      sx={{
                        bgcolor: STATUS_COLORS[ticket.status],
                        color: "common.white",
                        fontWeight: 500,
                        fontSize: 11,
                      }}
                    />
                  </DSTableCell>
                  {!isMobile && (
                    <DSTableCell>
                      <DSBox
                        sx={{
                          display: "flex",
                          gap: 0.5,
                          flexWrap: "wrap",
                        }}
                      >
                        {ticket.labels.map((label) => (
                          <DSChip
                            key={label}
                            label={label}
                            size="small"
                            variant="outlined"
                          />
                        ))}
                      </DSBox>
                    </DSTableCell>
                  )}
                  <DSTableCell>
                    {resolveUserName(ticket.assignee, users) || "-"}
                  </DSTableCell>
                  {!isMobile && (
                    <DSTableCell>{resolvedDate(ticket)}</DSTableCell>
                  )}
                </DSTableRow>
              ))}
            </DSTableBody>
          </DSTable>
        </DSTableContainer>
      )}

      <LoadMoreFooter
        loadedCount={loadedCount}
        total={total}
        hasMore={hasMore}
        onLoadMore={loadMore}
      />

      {selectedTicket && (
        <TicketDetailModal
          ticket={selectedTicket}
          onClose={() => setSelectedTicket(null)}
        />
      )}
    </DSBox>
  );
}
